export const APP_SCREEN = Object.freeze({
  MAIN: 'main',
  CONSULTATION: 'consultation',
  REPORT: 'report',
  CARE_CONSULT: 'careConsult',
  MEMBER_HUB: 'memberHub',
  MEMBER_DETAIL: 'memberDetail',
  WORKOUT_RECORDS: 'workoutRecords',
  PT_SESSION: 'ptSession',
  SELF_CHECKIN_DETAIL: 'selfCheckinDetail',
  WEEKLY_DETAIL: 'careWeeklyDetail',
  MEASUREMENT_START: 'careMeasurementStart',
  MEASUREMENT_DETAIL: 'careMeasurementDetail',
  PRESCRIPTION: 'prescription',
  ONBOARDING: 'onboarding',
  TRAINER_SCHEDULE: 'trainerSchedule',
  TRAINER_SCHEDULE_PLANNER: 'trainerSchedulePlanner',
  INBOX: 'inbox',
  SETTINGS: 'settings',
  ADMIN: 'admin',
});

export const APP_SCREEN_VALUES = Object.freeze(Object.values(APP_SCREEN));

export const APP_SHELL_SCREENS = Object.freeze([
  APP_SCREEN.MAIN,
  APP_SCREEN.TRAINER_SCHEDULE,
  APP_SCREEN.TRAINER_SCHEDULE_PLANNER,
  APP_SCREEN.CARE_CONSULT,
  APP_SCREEN.SETTINGS,
  APP_SCREEN.INBOX,
]);

export const APP_SHELL_NO_PADDING_SCREENS = Object.freeze([
  APP_SCREEN.MAIN,
  APP_SCREEN.TRAINER_SCHEDULE,
  APP_SCREEN.TRAINER_SCHEDULE_PLANNER,
  APP_SCREEN.CARE_CONSULT,
  APP_SCREEN.INBOX,
]);

export const APP_EDGE_TO_EDGE_SCREENS = Object.freeze([
  APP_SCREEN.MEMBER_HUB,
  APP_SCREEN.MEASUREMENT_START,
  APP_SCREEN.MEASUREMENT_DETAIL,
  APP_SCREEN.WEEKLY_DETAIL,
  APP_SCREEN.MEMBER_DETAIL,
  APP_SCREEN.WORKOUT_RECORDS,
  APP_SCREEN.PT_SESSION,
  APP_SCREEN.SELF_CHECKIN_DETAIL,
  APP_SCREEN.PRESCRIPTION,
  APP_SCREEN.ONBOARDING,
]);

/* FINAL FREEZE viewport owners fill the complete area below the App header.
 * App-level block padding would create a second document scroller and move
 * viewport-fixed actions with the outer document. */
export const APP_FIXED_VIEWPORT_SCREENS = Object.freeze([
  APP_SCREEN.CONSULTATION,
]);

export function resolveTrainerWorkflowContainerKey(screen) {
  return `trainer-workflow-${isAppScreen(screen) ? screen : APP_SCREEN.MAIN}`;
}

export const GLOBAL_HEADER_HIDDEN_SCREENS = Object.freeze([
  APP_SCREEN.REPORT,
  APP_SCREEN.MEMBER_DETAIL,
  APP_SCREEN.MEMBER_HUB,
  APP_SCREEN.MEASUREMENT_START,
  APP_SCREEN.WORKOUT_RECORDS,
  APP_SCREEN.PT_SESSION,
  APP_SCREEN.SELF_CHECKIN_DETAIL,
  APP_SCREEN.PRESCRIPTION,
]);

export const HUB_TRANSIENT_RETURN_SCREENS = Object.freeze([
  APP_SCREEN.WEEKLY_DETAIL,
]);

export const APP_HASH = Object.freeze({
  PRICING: '#pricing',
  CARE_HOME_PREVIEW: '#care-home-preview',
  CARE_MEMBER_PREVIEW: '#care-member-preview',
  TRAINER_SCHEDULE: '#trainer-schedule',
  TRAINER_SCHEDULE_PLANNER: '#trainer-schedule-planner',
});

const LAYER365_ENTRY_HASHES = Object.freeze([
  '#auth',
  '#home',
  '#conversations',
  '#onboarding',
  '#profile',
  '#unavailable',
  '#center-search',
  '#center-register',
  '#center-affiliation',
]);

const AUTHENTICATED_DIRECT_HASHES = Object.freeze([
  APP_HASH.TRAINER_SCHEDULE,
  APP_HASH.TRAINER_SCHEDULE_PLANNER,
]);

const LIVE_PREVIEW_HASHES = Object.freeze([
  APP_HASH.CARE_HOME_PREVIEW,
  APP_HASH.CARE_MEMBER_PREVIEW,
]);

function normalizeHash(hash = '') {
  return String(hash || '').trim();
}

export function isAppScreen(screen) {
  return APP_SCREEN_VALUES.includes(screen);
}

export function resolveAppScreen(screen, fallback = APP_SCREEN.MAIN) {
  if (isAppScreen(screen)) return screen;
  return isAppScreen(fallback) ? fallback : APP_SCREEN.MAIN;
}

export function resolveHubBackNavigation({
  transientScreen = null,
  origin = null,
  fallback = APP_SCREEN.MAIN,
} = {}) {
  if (HUB_TRANSIENT_RETURN_SCREENS.includes(transientScreen)) {
    return { screen: transientScreen, preserveHubContext: true };
  }

  const requestedFallback = resolveAppScreen(fallback);
  const fallbackScreen = requestedFallback === APP_SCREEN.MEMBER_HUB
    ? APP_SCREEN.MAIN
    : requestedFallback;
  const originScreen = resolveAppScreen(origin, fallbackScreen);
  return {
    screen: originScreen === APP_SCREEN.MEMBER_HUB ? fallbackScreen : originScreen,
    preserveHubContext: false,
  };
}

export function isPricingHash(hash) {
  return normalizeHash(hash) === APP_HASH.PRICING;
}

export function isCareHomePreviewHash(hash) {
  return normalizeHash(hash) === APP_HASH.CARE_HOME_PREVIEW;
}

export function isCareMemberPreviewHash(hash) {
  return normalizeHash(hash) === APP_HASH.CARE_MEMBER_PREVIEW;
}

export function isLivePreviewHash(hash) {
  return LIVE_PREVIEW_HASHES.includes(normalizeHash(hash));
}

export function classifyProtectedHash(hash) {
  const safeHash = normalizeHash(hash);
  if (isPricingHash(safeHash)) return 'public-service';
  if (isLivePreviewHash(safeHash)) return 'live-preview';
  return null;
}

export function matchSharedReportPath(pathname) {
  const match = String(pathname || '').match(/^\/share\/([^/]+)(?:\/week\/(\d+))?$/);
  if (!match) return null;
  return {
    token: match[1],
    weekNumber: match[2] ? Number(match[2]) : null,
  };
}

export function matchSelfCheckinHash(hash) {
  const match = normalizeHash(hash).match(/^#self\/([A-Za-z0-9_-]+)$/);
  return match ? { token: match[1] } : null;
}

export function isLayer365EntryHash(hash) {
  const safeHash = normalizeHash(hash);
  return (
    LAYER365_ENTRY_HASHES.includes(safeHash)
    || /^#conversations\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(safeHash)
    || /^#center-(?:search|map)(?:\?.*)?$/.test(safeHash)
    || /^#center\/\d+(?:\/trainers|\/trainer\/[A-Za-z0-9-]+)?$/.test(safeHash)
    || /^#center-(?:affiliation|management|applications|recruitment)\/\d+(?:\/\d+)?$/.test(safeHash)
    || /^#inquiry\/new\/\d+\/[A-Za-z0-9-]+$/.test(safeHash)
    || /^#inquiry\/\d+$/.test(safeHash)
    || /^#pt-invite-issue\/\d+$/.test(safeHash)
    || /^#pt-invite\/pt_[A-Za-z0-9_-]{32,509}$/.test(safeHash)
    || /^#pt-room\/\d+(?:\/manage)?$/.test(safeHash)
    || /^#assignment-change\/\d+$/.test(safeHash)
    || /^#center-affiliation-invite\/center_[A-Za-z0-9_-]{32,505}$/.test(safeHash)
  );
}

export function matchOnboardingRecordHash(hash) {
  const match = normalizeHash(hash).match(/^#onboarding\/(\d+)$/);
  const recordId = match ? Number(match[1]) : 0;
  return recordId ? { recordId } : null;
}

export function matchAssistantHash(hash) {
  const match = normalizeHash(hash).match(/^#assistant\/(\d+)(?:\/(kpi|plan|adjustment|autonomous|meal))?$/);
  const recordId = match ? Number(match[1]) : 0;
  return recordId ? { recordId, section: match[2] || 'kpi' } : null;
}

export function matchMemberHubHash(hash) {
  const match = normalizeHash(hash).match(/^#hub\/(\d+)$/);
  const recordId = match ? Number(match[1]) : 0;
  return recordId ? { recordId } : null;
}

export function isAuthenticatedDirectHash(hash) {
  const safeHash = normalizeHash(hash);
  return (
    AUTHENTICATED_DIRECT_HASHES.includes(safeHash)
    || !!matchOnboardingRecordHash(safeHash)
    || !!matchAssistantHash(safeHash)
    || !!matchMemberHubHash(safeHash)
    || safeHash.startsWith('#report=')
    || safeHash.startsWith('#share=')
  );
}

export function classifyRouteAccess({ pathname = '/', hash = '' } = {}) {
  const safeHash = normalizeHash(hash);
  const protectedHash = classifyProtectedHash(safeHash);
  if (protectedHash) return protectedHash;
  if (matchSharedReportPath(pathname) || matchSelfCheckinHash(safeHash)) {
    return 'public-service';
  }
  if (isAuthenticatedDirectHash(safeHash)) return 'authenticated-deep-link';
  return 'authenticated-app';
}
